import type { OAuthTokens } from '@/lib/platform/generated/bindings.gen';

const REFRESH_FRACTION = 0.8;
const MIN_DELAY_MS = 30_000;
const MAX_DELAY_MS = 24 * 3600 * 1000;
const RETRY_DELAY_MS = 60_000;

/**
 * How long to wait before proactively refreshing an access token, given its
 * `expires_in` (seconds). We refresh at 80% of the lifetime so a fresh token is
 * always minted before the old one expires, floored so a pathologically short
 * lifetime can't turn into a hot loop against the token endpoint, and ceilinged
 * so a very long lifetime stays inside a sane `setTimeout` budget.
 */
export function refreshDelayMs(expiresInSeconds: number): number {
  const raw = expiresInSeconds * REFRESH_FRACTION * 1000;
  return Math.min(MAX_DELAY_MS, Math.max(MIN_DELAY_MS, raw));
}

interface TokenRefreshDeps {
  /** Read the persisted refresh token (null when signed out). */
  getRefreshToken: () => Promise<string | null>;
  /** Exchange a refresh token for a fresh token set. */
  refreshAccessToken: (refreshToken: string) => Promise<OAuthTokens>;
  /**
   * Apply a freshly minted token set: update the auth store, re-push the access
   * token to the native HTTP clients, and persist the rotated refresh token.
   */
  applyTokens: (tokens: OAuthTokens) => Promise<void>;
}

let deps: TokenRefreshDeps | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;

/** Wire the scheduler's dependencies once at app startup. */
export function configureTokenRefresh(next: TokenRefreshDeps): void {
  deps = next;
}

/** Cancel any pending proactive refresh (e.g. on logout). */
export function cancelTokenRefresh(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

/**
 * Arm a proactive access-token refresh to fire before the current token
 * expires. Replaces any previously-armed refresh, so it is safe to call after
 * every successful token mint. Self-perpetuating: each successful refresh
 * re-arms from the new token's lifetime, so a long-lived session keeps a valid
 * token in the native clients indefinitely (the fix for LUMEN-4E).
 */
export function scheduleTokenRefresh(expiresInSeconds: number): void {
  cancelTokenRefresh();
  timer = setTimeout(() => {
    void runRefresh();
  }, refreshDelayMs(expiresInSeconds));
}

async function runRefresh(): Promise<void> {
  timer = null;
  if (!deps) return;

  try {
    const refreshToken = await deps.getRefreshToken();
    if (!refreshToken) return;

    const tokens = await deps.refreshAccessToken(refreshToken);
    await deps.applyTokens(tokens);
    scheduleTokenRefresh(tokens.expires_in);
  } catch (err) {
    // A refresh failure (network blip, transient Auth0 error) must NOT leave
    // the scheduler dead — that would silently re-introduce the stale-token
    // bug. Retry after a bounded backoff.
    console.warn('[token-refresh] proactive refresh failed — retrying', err);
    cancelTokenRefresh();
    timer = setTimeout(() => {
      void runRefresh();
    }, RETRY_DELAY_MS);
  }
}
